import { ChangeEvent, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { IPrescription } from '@/types';
import { Button } from '../ui/button';
import { Form, FormField, FormItem, FormControl, FormMessage } from '../ui/form';
import { Input } from '../ui/input';

interface WizardProps {
  prescription: IPrescription | null;
  setIsValidated: (isValidated: boolean) => void;
  setPrescription: (prescription: IPrescription) => void;
}

const FormSchema = z.object({
  dobMM: z.string().regex(/^(0[1-9]|1[0-2])$/, 'Invalid month'),
  dobDD: z.string().regex(/^(0[1-9]|[12][0-9]|3[01])$/, 'Invalid day'),
  dobYYYY: z
    .string()
    .regex(/^(19|20)\d{2}$/, 'Invalid year')
    .refine((year) => {
      const currentYear = new Date().getFullYear();
      return parseInt(year) >= 1900 && parseInt(year) <= currentYear;
    }, 'Year must be between 1900 and the current year'),
});

const Verification = ({ setIsValidated, setPrescription }: WizardProps) => {
  const code = window.location.pathname.split('/').pop()?.toLocaleLowerCase() || '';
  const [error, setError] = useState<string | null>(null);
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      dobMM: '',
      dobDD: '',
      dobYYYY: '',
    },
  });

  const mmRef = useRef<HTMLInputElement>(null);
  const ddRef = useRef<HTMLInputElement>(null);
  const yyyyRef = useRef<HTMLInputElement>(null);

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    setError(null);
    const dobString = `${data.dobYYYY}-${data.dobMM}-${data.dobDD}`;

    try {
      const response = await axios.post(`${import.meta.env.VITE_NEEDLE_API_URL}/v1/sessions/sso`, {
        dob: dobString,
        code: code,
      });

      if (response.data.error) {
        setError('Birthdate does not match our records.');
        return;
      }

      if (!response.data) {
        setError('Birthdate does not match our records.');
        return;
      }

      if (response.data.token && response.data.user) {
        const rx_response = await axios.get(`${import.meta.env.VITE_NEEDLE_API_URL}/v2/rx/${code}`, {
          headers: {
            'Needle-Token': response.data.token,
          },
        });

        if (!rx_response.data) {
          setError('Prescription not found.');
          return;
        }

        const payload = {
          sessionId: response.data.token,
          currentUser: response.data.user,
        };
        window.localStorage.setItem('Needle', JSON.stringify(payload));

        setIsValidated(true);
        setPrescription(rx_response.data);
      }
    } catch (error) {
      setError('Birthdate does not match our records.');
      return;
    }
  };

  const handleDobMMChange = (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (value) {
      const numValue = parseInt(value, 10);
      if (numValue > 12) value = '12';
    }
    form.setValue('dobMM', value);

    if (value.length === 2) {
      ddRef.current?.focus();
    }
  };

  const handleDobDDChange = (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (value) {
      const numValue = parseInt(value, 10);
      if (numValue > 31) value = '31';
    }
    form.setValue('dobDD', value);

    if (value.length === 2) {
      yyyyRef.current?.focus();
    }
  };

  const handleDobYYYYChange = (e: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    let value = e.target.value.replace(/\D/g, '').slice(0, 4);
    const currentYear = new Date().getFullYear();

    if (value.length === 4) {
      const numValue = parseInt(value, 10);
      if (numValue < 1900) value = '1900';
      if (numValue > currentYear) value = currentYear.toString();

      if (numValue >= 1900 && numValue <= currentYear) {
        form.setValue('dobYYYY', value);
        form.trigger('dobYYYY');
        form.handleSubmit(onSubmit)();
      }
    } else {
      form.setValue('dobYYYY', value);
    }
  };

  const handleDobKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, field: 'MM' | 'DD' | 'YYYY') => {
    setError(null);
    if (e.key === 'Backspace') {
      if (field === 'DD' && form.getValues('dobDD').length === 0) {
        mmRef.current?.focus();
      }
      if (field === 'YYYY' && form.getValues('dobYYYY').length === 0) {
        ddRef.current?.focus();
      }
    }
  };

  const handleDobMMBlur = () => {
    setError(null);
    let value = form.getValues('dobMM');
    if (value.length === 1 && parseInt(value, 10) < 10) {
      value = `0${value}`;
    }
    form.setValue('dobMM', value);
    if (value.length === 2) {
      ddRef.current?.focus();
    }
  };

  const handleDobDDBlur = () => {
    let value = form.getValues('dobDD');
    if (value.length === 1) {
      value = `0${value}`;
    }
    form.setValue('dobDD', value);
    if (value.length === 2) {
      yyyyRef.current?.focus();
    }
  };

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex gap-2 mt-8 mb-8 ">
            <div className="flex-1">
              <FormField
                name="dobMM"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="MM"
                        maxLength={2}
                        className="w-full text-2xl font-bold text-center h-14 placeholder:text-gray-400"
                        {...field}
                        ref={mmRef}
                        onChange={handleDobMMChange}
                        onBlur={handleDobMMBlur}
                        onKeyDown={(e) => handleDobKeyDown(e, 'MM')}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex-1">
              <FormField
                name="dobDD"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="DD"
                        maxLength={2}
                        className="w-full text-2xl font-bold text-center h-14 placeholder:text-gray-400"
                        {...field}
                        ref={ddRef}
                        onChange={handleDobDDChange}
                        onBlur={handleDobDDBlur}
                        onKeyDown={(e) => handleDobKeyDown(e, 'DD')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex-1">
              <FormField
                name="dobYYYY"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="YYYY"
                        maxLength={4}
                        className="w-full text-2xl font-bold text-center h-14 placeholder:text-gray-400"
                        {...field}
                        ref={yyyyRef}
                        onChange={handleDobYYYYChange}
                        onKeyDown={(e) => handleDobKeyDown(e, 'YYYY')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          {error && (
            <>
              <div className="flex justify-center w-full mt-4">
                <FormMessage>{error}</FormMessage>
              </div>
            </>
          )}

          <Button type="submit" className="w-full mt-4 rounded-md" disabled={!form.formState.isValid}>
            Verify Identity
          </Button>
        </form>
      </Form>
    </>
  );
};

export default Verification;
